import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Select from "react-select";
import API_URL from "../Config";
import "../forms/UserForm.css";
import "./miIndex.css";
import {
  MI_EVALUATION_TYPE_OPTIONS,
  fetchMiLocations,
  parseMiApiError,
} from "./miIndexUtils";

function AddMIEvaluation({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    location_id: "",
    evaluation_date: new Date().toISOString().split("T")[0],
    evaluator_name: "",
    evaluator_designation: "",
    evaluation_type: "Annual",
    remarks: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const locationOptions = useMemo(
    () =>
      (locations || []).map((location) => ({
        value: location.location_id,
        label: `${location.location_name} (${location.location_type})`,
        region: location.region,
        locationType: location.location_type,
      })),
    [locations],
  );

  const selectedLocation = useMemo(
    () =>
      locationOptions.find((option) => option.value === formData.location_id) ||
      null,
    [formData.location_id, locationOptions],
  );

  const evaluationTypeOptions = useMemo(
    () =>
      MI_EVALUATION_TYPE_OPTIONS.map((type) => ({
        value: type,
        label: type,
      })),
    [],
  );

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await fetchMiLocations({ per_page: 1000 });
        setLocations(data.locations || []);
      } catch (error) {
        showSnackbar(
          parseMiApiError(error, "Failed to load locations"),
          "error",
        );
      }
    };

    loadLocations();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.location_id) {
      validationErrors.location_id = "Location is required.";
    }
    if (!formData.evaluation_date) {
      validationErrors.evaluation_date = "Evaluation date is required.";
    }
    if (!formData.evaluator_name.trim()) {
      validationErrors.evaluator_name = "Evaluator name is required.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleCreateEvaluation = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const response = await axios.post(
        `${API_URL}/api/mi/evaluations`,
        {
          location_id: formData.location_id,
          evaluation_date: formData.evaluation_date,
          evaluator_name: formData.evaluator_name.trim(),
          evaluator_designation: formData.evaluator_designation.trim() || null,
          evaluation_type: formData.evaluation_type,
          remarks: formData.remarks.trim() || null,
        },
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
        },
      );

      showSnackbar(
        response.data?.message || "Evaluation created successfully",
        "success",
      );
      window.setTimeout(() => {
        navigate(`/mi-edit-evaluation/${response.data?.evaluation_id}`);
      }, 800);
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to create evaluation"),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const selectedLocationDetails = selectedLocation ? (
    <>
      {selectedLocation.locationType}
      {selectedLocation.region ? ` - ${selectedLocation.region}` : ""}
    </>
  ) : (
    "Select a location to begin"
  );

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New MI Evaluation</PageBackTitle>
              <div className="data-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-form-body">
            <form className="form-detail">
              <div className="new-data-form">
                <div className="form-group-select">
                  <label htmlFor="location_id">
                    Location <span className="text-danger">*</span>
                  </label>
                  <Select
                    inputId="location_id"
                    options={locationOptions}
                    value={selectedLocation}
                    onChange={(selectedOption) => {
                      setFormData((prevState) => ({
                        ...prevState,
                        location_id: selectedOption?.value || "",
                      }));
                      setErrors((prevState) => ({
                        ...prevState,
                        location_id: "",
                      }));
                    }}
                    placeholder="Select location"
                    isClearable
                    classNamePrefix="react-select"
                  />
                  {errors.location_id && (
                    <div className="text-danger">{errors.location_id}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="evaluation_date">
                    Evaluation Date <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      id="evaluation_date"
                      name="evaluation_date"
                      type="date"
                      value={formData.evaluation_date}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errors.evaluation_date && (
                    <div className="text-danger">{errors.evaluation_date}</div>
                  )}
                </div>

                <div className="form-group-select">
                  <label htmlFor="evaluation_type">Evaluation Type</label>
                  <Select
                    inputId="evaluation_type"
                    options={evaluationTypeOptions}
                    value={
                      evaluationTypeOptions.find(
                        (option) => option.value === formData.evaluation_type,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setFormData((prevState) => ({
                        ...prevState,
                        evaluation_type: selectedOption?.value || "Annual",
                      }))
                    }
                    classNamePrefix="react-select"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="evaluator_name">
                    Evaluator Name <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      id="evaluator_name"
                      name="evaluator_name"
                      value={formData.evaluator_name}
                      onChange={handleInputChange}
                      placeholder="Enter evaluator name"
                    />
                  </div>
                  {errors.evaluator_name && (
                    <div className="text-danger">{errors.evaluator_name}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="evaluator_designation">
                    Evaluator Designation
                  </label>
                  <div className="input-group">
                    <input
                      id="evaluator_designation"
                      name="evaluator_designation"
                      value={formData.evaluator_designation}
                      onChange={handleInputChange}
                      placeholder="Enter evaluator designation"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="remarks">Remarks</label>
                  <div className="input-group">
                    <textarea
                      id="remarks"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder="Enter remarks"
                    />
                  </div>
                </div>

                {/* <div className="form-group">
                  <label htmlFor="location_details">Location Details</label>
                  <div className="input-group">
                    <input
                      id="location_details"
                      type="text"
                      value={selectedLocationDetails}
                      readOnly
                    />
                  </div>
                </div> */}
              </div>
            </form>
          </div>

          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleCreateEvaluation}
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create And Continue"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={() => navigate("/mi-evaluations")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default AddMIEvaluation;
